import { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { createClient } from '@supabase/supabase-js';
import { useSearchParams } from 'react-router-dom';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface ContactMessage {
  id: string;
  contact_id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
}

interface Contact {
  id: string;
  customer_name: string;
  customer_email: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
}

export default function ContactMessagesPage() {
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Get ID from URL parameter
  useEffect(() => {
    const idParam = searchParams.get('id');
    
    if (idParam) {
      // Remove '#' symbol if present
      const decodedId = decodeURIComponent(idParam).replace(/^#/, '');
      setSearchId(decodedId);
      setTimeout(() => {
        fetchContactWithId(decodedId);
      }, 100);
    }
  }, [searchParams]);

  // Fetch user's contact with ID parameter
  const fetchContactWithId = async (contactId: string) => {
    if (!contactId) {
      return;
    }
    
    setLoading(true);
    try {
      // Remove '#' symbol if present and trim
      const trimmedId = contactId.replace(/^#/, '').trim();
      
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', trimmedId)
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      setContacts(data ? [data] : []);
      
      // Auto-select the contact if available
      if (data) {
        await handleContactSelect(data);
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      alert('お問い合わせが見つかりませんでした。Contact IDを確認してください。');
    } finally {
      setLoading(false);
    }
  };

  // Fetch contact based on ID
  const fetchData = async () => {
    // Remove '#' symbol if present and trim
    const trimmedId = searchId.replace(/^#/, '').trim();
    if (!trimmedId) {
      alert('Contact IDを入力してください。');
      return;
    }
    
    await fetchContactWithId(trimmedId);
  };

  // Fetch messages for selected contact
  const fetchContactMessages = async (contactId: string) => {
    try {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    }
  };

  // Send contact message
  const sendContactMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;

    try {
      const { error } = await supabase
        .from('contact_messages')
        .insert([
          {
            contact_id: selectedContact.id,
            sender_type: 'user',
            message: newMessage.trim()
          }
        ]);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      setNewMessage('');
      await fetchContactMessages(selectedContact.id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('メッセージの送信に失敗しました。もう一度お試しください。');
    }
  };

  // Handle contact selection
  const handleContactSelect = async (contact: Contact) => {
    setSelectedContact(contact);
    await fetchContactMessages(contact.id);
  };

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-600';
      case 'closed': return 'bg-gray-600';
      default: return 'bg-gray-600';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'open': return 'Open';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Tokyo'
    });
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <main className="py-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Contact Messages
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              View your conversations with our support team. Track your inquiries and receive assistance.
            </p>
          </div>
        </section>

        {/* Messages Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* List Sidebar */}
              <div className="lg:col-span-1">
                <Card className="p-6 h-full">
                  <h2 className="text-xl font-bold text-white mb-6">
                    Your Inquiry
                  </h2>
                  
                  {/* ID Search */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Enter Your Contact ID
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={searchId}
                        onChange={(e) => setSearchId(e.target.value)}
                        className="flex-1 bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        placeholder="Contact ID"
                      />
                      <Button 
                        onClick={fetchData}
                        disabled={!searchId || loading}
                        className="whitespace-nowrap"
                      >
                        {loading ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          <i className="ri-search-line"></i>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Contacts List */}
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {contacts.length === 0 && searchId && !loading && (
                      <div className="text-center py-8">
                        <i className="ri-inbox-line text-4xl text-gray-600 mb-3"></i>
                        <p className="text-gray-400">No inquiry found with this ID</p>
                      </div>
                    )}
                    
                    {contacts.map((contact) => (
                      <button
                        key={contact.id}
                        onClick={() => handleContactSelect(contact)}
                        className={`w-full text-left p-4 rounded-lg border transition-all cursor-pointer ${
                          selectedContact?.id === contact.id
                            ? 'border-teal-600 bg-teal-900/20'
                            : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-white font-medium text-sm">{contact.subject}</span>
                          <span className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(contact.status)}`}>
                            {getStatusText(contact.status)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-400 mb-1 line-clamp-2">
                          {contact.message}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(contact.created_at)}
                        </p>
                      </button>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Message Thread */}
              <div className="lg:col-span-2">
                <Card className="p-0 h-full min-h-[600px] flex flex-col">
                  {selectedContact ? (
                    <>
                      {/* Header */}
                      <div className="p-6 border-b border-gray-700">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-semibold text-white mb-2">
                              {selectedContact.subject} - {selectedContact.customer_name}
                            </h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-400">
                              <span className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(selectedContact.status)}`}>
                                {getStatusText(selectedContact.status)}
                              </span>
                              <span>{formatDate(selectedContact.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Original Message */}
                        <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                          <p className="text-xs text-gray-400 mb-2">Original Inquiry:</p>
                          <p className="text-sm text-gray-300 whitespace-pre-wrap">{selectedContact.message}</p>
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="flex-1 p-6 overflow-y-auto max-h-96">
                        <div className="space-y-4">
                          {messages.length === 0 && (
                            <div className="text-center py-8">
                              <i className="ri-chat-3-line text-4xl text-gray-600 mb-3"></i>
                              <p className="text-gray-400">No messages yet. Start the conversation!</p>
                            </div>
                          )}
                          
                          {messages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                                message.sender_type === 'user'
                                  ? 'bg-teal-600 text-white'
                                  : 'bg-gray-700 text-white'
                              }`}>
                                <p className="text-sm">{message.message}</p>
                                <p className={`text-xs mt-2 ${
                                  message.sender_type === 'user'
                                    ? 'text-teal-100'
                                    : 'text-gray-400'
                                }`}>
                                  {message.sender_type === 'user' ? 'You' : 'Support'} • {formatDate(message.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      </div>

                      {/* Message Input */}
                      <div className="p-6 border-t border-gray-700">
                        <div className="flex space-x-3">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                sendContactMessage();
                              }
                            }}
                            className="flex-1 bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            placeholder="Type your message..."
                          />
                          <Button 
                            onClick={sendContactMessage}
                            disabled={!newMessage.trim()}
                            className="whitespace-nowrap"
                          >
                            <i className="ri-send-plane-fill"></i>
                          </Button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          <i className="ri-information-line mr-1"></i>
                          Our support team typically responds within 24 hours during business days.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <i className="ri-message-3-line text-6xl text-gray-600 mb-4"></i>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          Select an Inquiry
                        </h3>
                        <p className="text-gray-400">
                          Choose an inquiry from the list to start messaging
                        </p>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Help Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Need Help?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-900 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold mb-2 text-white">24/7 Support</h3>
                <p className="text-gray-400 text-sm">Our support team is available 24/7 to answer your questions.</p>
              </div>
              
              <div className="bg-gray-900 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold mb-2 text-white">Secure Communication</h3>
                <p className="text-gray-400 text-sm">All messages are encrypted and secure. Your privacy is our top priority.</p>
              </div>
              
              <div className="bg-gray-900 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold mb-2 text-white">Fast Response</h3>
                <p className="text-gray-400 text-sm">We aim to respond to all messages within 1 hour during business hours.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
